import Service from '@ember/service';
import TemplateModel from 'cio/models/template';
import LayoutModel from 'cio/models/layout';
import { inject as service } from '@ember/service';
import FeaturesService from 'ember-feature-flags/services/features';
import { isVisualEditorInAppMessage } from 'cio/utils/in-app/message-config';
import { DEFAULT_IN_APP_TEMPLATE_BODY } from 'cio/utils/in-app/preview';

export const DRAG_AND_DROP_ICON_STRING = 'editor-drag-and-drop';
export const RICH_TEXT_ICON_STRING = 'editor-rich-text';
export const CODE_ICON_STRING = 'editor-code';

export enum Editor {
  DRAG_AND_DROP = 'bee',
  RICH_TEXT = 'wysiwyg',
  VISUAL = 'visual',
  CODE = 'html',
  JSON = 'json',
  SIMPLE = 'simple',
  RICH = 'rich', // rich push
  JS = 'js',
  DEFAULT = '',
}

export const EditorNameMap: Record<Editor, string> = {
  bee: 'Drag n Drop',
  wysiwyg: 'Rich Text',
  html: 'Code',
  visual: 'Visual',
  json: 'JSON',
  simple: 'Simple',
  rich: 'Rich Push',
  js: 'JavaScript',
  '': '',
};

export type EmailEditor = Exclude<Editor, Editor.JSON | Editor.SIMPLE | Editor.DEFAULT | Editor.JS | Editor.RICH>;
export type InAppEditor = Editor.JSON | Editor.VISUAL;

export abstract class BaseTemplate {
  abstract id: string;
  abstract body: string;
  abstract bodyJson: string;
  abstract layout: LayoutModel | string;
  abstract editor: Editor;
}

export class EmptyBeeTemplate implements BaseTemplate {
  id = 'empty';
  body = '';
  bodyJson = '';
  layout = '';
  editor = Editor.DRAG_AND_DROP;
}

export default class EditorService extends Service {
  @service features!: FeaturesService;

  get isInAppVisualEditorEnabled() {
    return this.features.isEnabled('in_app_visual_editor_enabled');
  }

  getInAppComposerForEditor(editor: Editor) {
    if (!this.isInAppVisualEditorEnabled) {
      return 'in-app/classic';
    }

    switch (editor) {
      case 'json':
        return 'in-app/classic';
      case 'visual':
        return 'in-app/parcel';
      default:
        return 'in-app/choose-template';
    }
  }

  getEmailComposerForEditor(editor: Editor) {
    if (editor === 'bee') {
      return 'email/drag-and-drop';
    } else if (editor === 'wysiwyg' || editor === 'html') {
      return 'email/text-and-code';
    } else if (editor === 'visual') {
      return 'email/visual';
    } else {
      return 'email/choose-template';
    }
  }

  getIconFor(template: TemplateModel) {
    if (template) {
      let icon;
      let { editor } = template;

      if (editor === 'bee') {
        icon = DRAG_AND_DROP_ICON_STRING;
      } else if (editor === 'wysiwyg') {
        icon = RICH_TEXT_ICON_STRING;
      } else if (editor === 'html' || isVisualEditorInAppMessage(template)) {
        icon = CODE_ICON_STRING;
      }

      return icon;
    } else {
      return null;
    }
  }

  getEmptyTemplate(template: TemplateModel, editor: Editor) {
    if (editor === 'bee') {
      return new EmptyBeeTemplate();
    } else if (editor === Editor.VISUAL && template.templateType === 'in_app') {
      template.set('bodyJson', JSON.stringify({ carta_content: DEFAULT_IN_APP_TEMPLATE_BODY }));
    }

    return template;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    editor: EditorService;
  }
}
